import { Box, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import Fonts from './Fonts';
import { themeColors } from '../styles/theme';
import TopNav from './TopNav';
import AdSense from './AdSense';
import ScrollIndicator from './ScrollIndicator';
import { Footer } from './Footer';

interface PrivacyPolicyProps {
    setTitle: Dispatch<SetStateAction<string>>;
    setShowBurgerMenuIcon: Dispatch<SetStateAction<boolean>>;
}

const PrivacyPolicy = ({
   setTitle,
   setShowBurgerMenuIcon
}: PrivacyPolicyProps) => {
    const [localTitle, setLocalTitle] = useState<string>("Privacy Policy");

    useEffect(() => {
        setTitle("Privacy Policy");
        setLocalTitle("Privacy Policy");
        setShowBurgerMenuIcon(true);
        
        // Update document title for SEO
        document.title = "Privacy Policy | Suntap";
        
        // Scroll to the top of the page
        window.scrollTo(0, 0);
    }, // eslint-disable-next-line
        []);

    return (
        <>
        <Helmet>
            <title>Privacy Policy | Suntap</title>
            <meta name="description" content="Read Suntap's Privacy Policy to understand how we collect, use, and protect your personal information when using our pub garden sun tracking service." />
            <link rel="canonical" href="https://sun-tap.com/privacy-policy" />
            <meta name="keywords" content="privacy policy, suntap privacy, data protection, personal information, privacy terms, user data, gdpr compliance" />
            <meta property="og:title" content="Privacy Policy | Suntap" />
            <meta property="og:description" content="Read Suntap's Privacy Policy to understand how we collect, use, and protect your personal information." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://sun-tap.com/privacy-policy" />
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content="Privacy Policy | Suntap" />
            <meta name="twitter:description" content="Read Suntap's Privacy Policy to understand how we collect, use, and protect your personal information." />
        </Helmet>
        <Fonts />
        <TopNav title={localTitle} />

        <Box 
            sx={{
                width: '100%',
                maxWidth: '900px',
                margin: '0 auto',
                padding: { xs: '0px 16px 16px', sm: '0px 24px 20px' },
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mt: 1,
            }}
        >
            {/* Text Content Box */}
            <Typography
                variant="h1"
                sx={{
                    fontSize: '1.75rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Privacy Policy
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Last updated 15 April 2025.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Introduction
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Welcome to Suntap. This policy explains how we handle your data while you search for the perfect sunny spot for your pint. We've tried to keep this as clear and straightforward as possible.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Who We Are
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Suntap is the data controller responsible for your personal data. If you have questions about this privacy policy or our data practices, please contact us at info@sun-tap.com.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Information We Collect
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Location Data
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                <ul style={{ paddingLeft: '20px', marginTop: '8px' }}>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Current location</strong>: With your permission, we collect your GPS coordinates to show nearby pubs. You can allow or deny this permission.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Search locations</strong>: When you drop a pin or search an area, we temporarily process those coordinates.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Local storage</strong>: We may save your last location on your device to improve your experience. You can clear this through your browser settings at any time.
                    </li>
                </ul>
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                User Contributions
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Garden drawings</strong>: When you submit a pub garden drawing, we store those coordinates and elevation data.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Garden status</strong>: Information about whether a pub has a garden or not.
                    </li>
                </ul>
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Usage Information
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                <strong>Analytics</strong>: We use Google Analytics to understand how people use our app, including:
                <ul style={{ paddingLeft: '20px'}}>
                    <li style={{ marginBottom: '4px' }}>Search queries and patterns</li>
                    <li style={{ marginBottom: '4px' }}>Feature usage</li>
                    <li style={{ marginBottom: '4px' }}>Map interactions</li>
                    <li style={{ marginBottom: '4px' }}>View preferences (2D vs 3D)</li>
                    <li style={{ marginBottom: '4px' }}>Time spent viewing garden information</li>
                    <li style={{ marginBottom: '4px' }}>Sharing activities</li>
                    <li style={{ marginBottom: '4px' }}>Garden drawing and editing behavior</li>
                    <li style={{ marginBottom: '4px' }}>Performance metrics</li>
                    <li style={{ marginBottom: '4px' }}>Search and filter preferences</li>
                </ul>
                This data is anonymized and helps us improve the application.
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Technical Data
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Cache data</strong>: We store calculations locally for improved performance.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                        <strong>Session data</strong>: Information about your current visit.
                    </li>
                </ul>
            </Typography>

            <Typography
                variant="h3"
                sx={{
                    fontSize: '1.1rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Third-Party Services
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                    marginBottom: '16px'
                }}
            >
                <strong>Google Maps data</strong>: We use Google Maps to provide location services. Their privacy policy applies to this component.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                How We Use Your Data
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We use your information to:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>Display sunny pub gardens in your area</li>
                    <li style={{ marginBottom: '4px' }}>Improve our application based on usage patterns</li>
                    <li style={{ marginBottom: '4px' }}>Build our database of garden information</li>
                    <li style={{ marginBottom: '4px' }}>Maintain technical functionality</li>
                </ul>
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Legal Basis for Processing
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Under GDPR, we process your data based on:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}><strong>Contract fulfillment</strong>: To provide our service</li>
                    <li style={{ marginBottom: '4px' }}><strong>Legitimate interests</strong>: To improve the app (where this doesn't override your rights)</li>
                    <li style={{ marginBottom: '4px' }}><strong>Consent</strong>: For location tracking and optional features</li>
                </ul>
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Data Sharing
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                    marginBottom: '16px'
                }}
            >
                We do not sell your personal data. We share limited information with:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}><strong>Service providers</strong>: Partners who help us operate our service</li>
                    <li style={{ marginBottom: '4px' }}><strong>Google Maps</strong>: For location-based functionality</li>
                    <li style={{ marginBottom: '4px' }}><strong>Analytics partners</strong>: To help us understand usage patterns</li>
                </ul>
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Data Security
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We have implemented appropriate security measures to protect your data from unauthorized access, accidental loss, or alteration. We limit access to those who have a business need.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Data Retention
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We retain your personal data only as long as necessary for the purposes we collected it. Garden submissions become part of our database indefinitely (unless you request removal) as they benefit all users of the service.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Your Rights
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Under GDPR, you have the right to:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>Request access to your personal data</li>
                    <li style={{ marginBottom: '4px' }}>Request correction of inaccurate data</li>
                    <li style={{ marginBottom: '4px' }}>Request deletion of your data in certain circumstances</li>
                    <li style={{ marginBottom: '4px' }}>Restrict processing in certain circumstances</li>
                    <li style={{ marginBottom: '4px' }}>Data portability (receiving your data in a usable format)</li>
                    <li style={{ marginBottom: '4px' }}>Object to processing based on legitimate interests</li>
                </ul>
                To exercise any of these rights, please contact us at info@sun-tap.com.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Cookies and Storage
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Our application uses local storage to remember preferences and improve performance. We also use cookies for analytics purposes. You can manage these through your browser settings.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Age Restrictions
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                Our service is intended for individuals aged 18 and over. We don't knowingly collect data from anyone under 18.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Sun Safety and Responsible Drinking
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                While we're all about finding sunny spots, we encourage responsible enjoyment:
                <ul style={{ paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '4px' }}>Remember to apply sunscreen when enjoying those perfectly sunny beer gardens</li>
                    <li style={{ marginBottom: '4px' }}>Stay hydrated (and not just with beer)</li>
                    <li style={{ marginBottom: '4px' }}>Drink responsibly and know your limits</li>
                    <li style={{ marginBottom: '4px' }}>Never drink and drive</li>
                </ul>
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Policy Updates
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                We may update this policy periodically. When we make significant changes, we'll post the new version here with an updated date.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Complaints
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                If you have concerns about how we've handled your data, you can contact the Information Commissioner's Office (ICO), the UK supervisory authority for data protection issues (www.ico.org.uk). We'd appreciate the chance to address your concerns directly first.
            </Typography>

            <Typography
                variant="h2"
                sx={{
                    fontSize: '1.3rem',
                    fontFamily: "ApfelGrotezkRegular",
                    color: themeColors.darkBackground,
                    fontWeight: 600
                }}
            >
                Contact Us
            </Typography>

            <Typography
                variant="body1"
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.6,
                    color: themeColors.darkBackground,
                    fontFamily: "ApfelGrotezkRegular",
                }}
            >
                For questions about this privacy policy, please contact us at:
                <br />
                Email: info@sun-tap.com
            </Typography>

            {/* Advertisement Section - Not in a Paper 
            <Box sx={{ width: '100%', my: 1, borderRadius: '40px' }}>
                <AdSense
                    client="ca-pub-9983613093656329"
                    slot="2973327869"
                    format="auto"
                    responsive="true"
                    width="100%"
                    height="250px"
                />
            </Box>*/}

            <Footer />
        </Box>

        <ScrollIndicator
            forceVisible={true}
            iconColor={themeColors.orange}
            backgroundColor="rgba(255, 255, 255, 0.8)"
        />
        </>
    );
};

export default PrivacyPolicy;